export type Vec3 = [number, number, number];

/**
 * How the three angles of an Euler definition compose, all in degrees.
 *
 * - 'intrinsic-xyz': about the frame's own axes - X first, then the rotated
 *   Y', then the twice-rotated Z''. R = Rx * Ry * Rz. The CAD/CAE convention,
 *   and the one new systems are written with.
 * - 'extrinsic-xyz': about the fixed global axes in X, Y, Z order.
 *   R = Rz * Ry * Rx.
 *
 * An absent or unrecognised value reads as 'extrinsic-xyz', so a definition
 * stored before the field existed keeps the frame it was saved with.
 */
export type RotationConvention = 'intrinsic-xyz' | 'extrinsic-xyz';

export type CoordinateSystemDefinition =
  | { mode: 'points'; origin: number[]; pointPx: number[]; pointPy: number[] }
  | { mode: 'euler'; origin: number[]; rotation: number[]; rotationConvention?: RotationConvention };

export interface CoordinateSystem {
  id: string;
  name: string;
  definition: CoordinateSystemDefinition;
}

export interface LocalFramePoints {
  origin: Vec3;
  pointPx: Vec3;
  pointPy: Vec3;
}

export interface LocalFrameAxes {
  ex: Vec3;
  ey: Vec3;
  ez: Vec3;
}

// The optimizer rejects a frame only below 1e-12, which in millimetres admits
// frames that are numerically valid and physically meaningless. These bounds
// are deliberately stricter so the failure surfaces in the form, not as NaN
// loads in a finished run.
const MIN_AXIS_LENGTH = 1e-6;
const MIN_SIN_ANGLE = 1e-4;

function toVec3(value: number[] | undefined | null): Vec3 | null {
  if (!Array.isArray(value) || value.length !== 3) return null;
  if (!value.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  return [value[0], value[1], value[2]];
}

function subtract(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function norm(a: Vec3): number {
  return Math.sqrt(dot(a, a));
}

function scale(a: Vec3, k: number): Vec3 {
  return [a[0] * k, a[1] * k, a[2] * k];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
  ];
}

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function rotationConventionOf(definition: { rotationConvention?: unknown }): RotationConvention {
  return definition.rotationConvention === 'intrinsic-xyz' ? 'intrinsic-xyz' : 'extrinsic-xyz';
}

/**
 * First and second columns of the local-to-global rotation for an Euler
 * definition, written out explicitly so the composition order is not left to
 * a library's Euler convention. See RotationConvention for the two orders.
 */
function eulerColumns(rotation: number[], convention: RotationConvention): { ex: Vec3; ey: Vec3 } {
  const rx = degToRad(rotation[0]);
  const ry = degToRad(rotation[1]);
  const rz = degToRad(rotation[2]);

  const cx = Math.cos(rx);
  const sx = Math.sin(rx);
  const cy = Math.cos(ry);
  const sy = Math.sin(ry);
  const cz = Math.cos(rz);
  const sz = Math.sin(rz);

  if (convention === 'intrinsic-xyz') {
    // R = Rx * Ry * Rz.
    return {
      ex: [cy * cz, sx * sy * cz + cx * sz, sx * sz - cx * sy * cz],
      ey: [-cy * sz, cx * cz - sx * sy * sz, cx * sy * sz + sx * cz]
    };
  }

  // R = Rz * Ry * Rx.
  return {
    ex: [cz * cy, sz * cy, -sy],
    ey: [cz * sy * sx - sz * cx, sz * sy * sx + cz * cx, cy * sx]
  };
}

/**
 * The three points the optimizer expects. Euler definitions are converted
 * here; because the rotation columns are orthonormal, the optimizer's
 * Gram-Schmidt recovers exactly the rotation the user dialled in.
 */
export function deriveFramePoints(definition: CoordinateSystemDefinition): LocalFramePoints | null {
  const origin = toVec3(definition.origin);
  if (!origin) return null;

  if (definition.mode === 'points') {
    const pointPx = toVec3(definition.pointPx);
    const pointPy = toVec3(definition.pointPy);
    if (!pointPx || !pointPy) return null;
    return { origin, pointPx, pointPy };
  }

  const rotation = toVec3(definition.rotation);
  if (!rotation) return null;

  const { ex, ey } = eulerColumns(rotation, rotationConventionOf(definition));
  return {
    origin,
    pointPx: [origin[0] + ex[0], origin[1] + ex[1], origin[2] + ex[2]],
    pointPy: [origin[0] + ey[0], origin[1] + ey[1], origin[2] + ey[2]]
  };
}

/**
 * Port of the optimizer's frame construction.
 *
 * PointPy defines a plane, not the Y axis: it is orthogonalised against Ex.
 * Returns null for a degenerate frame, which the optimizer itself does not
 * detect and would solve as NaN.
 */
export function deriveAxes(points: LocalFramePoints): LocalFrameAxes | null {
  const a = subtract(points.pointPx, points.origin);
  const b = subtract(points.pointPy, points.origin);

  const normA = norm(a);
  if (normA < MIN_AXIS_LENGTH) return null;
  const ex = scale(a, 1 / normA);

  const normB = norm(b);
  if (normB < MIN_AXIS_LENGTH) return null;

  const bPerp = subtract(b, scale(ex, dot(b, ex)));
  const normBPerp = norm(bPerp);
  if (normBPerp / normB < MIN_SIN_ANGLE) return null;
  const ey = scale(bPerp, 1 / normBPerp);

  return { ex, ey, ez: cross(ex, ey) };
}

export function deriveAxesFromDefinition(definition: CoordinateSystemDefinition): LocalFrameAxes | null {
  const points = deriveFramePoints(definition);
  return points ? deriveAxes(points) : null;
}

/**
 * Human-readable reasons a system cannot be used. An empty array means the
 * system is safe to emit.
 */
export function validateCoordinateSystem(system: CoordinateSystem): string[] {
  const errors: string[] = [];

  if (!system.name || !system.name.trim()) {
    errors.push('Coordinate system needs a name.');
  }

  const points = deriveFramePoints(system.definition);
  if (!points) {
    errors.push('Every point needs three finite numbers.');
    return errors;
  }

  const a = subtract(points.pointPx, points.origin);
  const b = subtract(points.pointPy, points.origin);

  if (norm(a) < MIN_AXIS_LENGTH) {
    errors.push('Origin and X-axis point are coincident.');
  } else if (norm(b) < MIN_AXIS_LENGTH) {
    errors.push('Origin and plane point are coincident.');
  } else if (!deriveAxes(points)) {
    errors.push('Plane point is collinear with the X axis.');
  }

  return errors;
}

/**
 * Space-separated triple, the serialization the optimizer's StringToVec
 * expects for LocalCoordinate children.
 *
 * Components are rounded to nanometre precision first. Deriving axes from
 * angles leaves float noise - cos(90 degrees) is 6.1e-17, not 0 - and writing
 * that into the XML makes the output unreadable for no gain at this scale.
 */
export function formatFrameVector(value: Vec3): string {
  return value.map((n) => `${Number(n.toFixed(9))}`).join(' ');
}

/**
 * A frame whose x' runs along a picked face normal. The second point only has
 * to be off that axis; any world axis that is not nearly parallel will do.
 */
export function frameFromFaceNormal(origin: number[], normal: number[]): CoordinateSystemDefinition {
  const o = toVec3(origin) ?? [0, 0, 0];
  const n = toVec3(normal);
  const length = n ? norm(n) : 0;
  if (!n || length < MIN_AXIS_LENGTH) {
    return { mode: 'points', origin: o, pointPx: [o[0] + 1, o[1], o[2]], pointPy: [o[0], o[1] + 1, o[2]] };
  }

  const ex = scale(n, 1 / length);
  const candidates: Vec3[] = [[0, 0, 1], [0, 1, 0], [1, 0, 0]];
  const helper = candidates.find((c) => Math.abs(dot(c, ex)) < 0.9) ?? [0, 1, 0];

  return {
    mode: 'points',
    origin: o,
    pointPx: [o[0] + ex[0], o[1] + ex[1], o[2] + ex[2]],
    pointPy: [o[0] + helper[0], o[1] + helper[1], o[2] + helper[2]]
  };
}

/**
 * Coerces a persisted reference. Anything that is not a non-empty string means
 * the global frame, which is what every project predating this feature has.
 */
export function normalizeCoordinateSystemId(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** The project's model orientation (`userCoordinateSystem`): a turn in degrees about the global axes. */
export interface ModelOrientation {
  enabled?: boolean;
  origin?: number[];
  rotation?: number[];
}

/**
 * Applies the model orientation to a point: about the global X, then Y, then Z
 * axes (R = Rz * Ry * Rx), around the orientation's origin. This is the turn
 * the runner gives the geometry before the optimizer sees it.
 */
export function turnPoint(point: Vec3, orientation: ModelOrientation | null | undefined): Vec3 {
  if (!orientation?.enabled) return point;
  const rotation = toVec3(orientation.rotation ?? null) ?? [0, 0, 0];
  const center = toVec3(orientation.origin ?? null) ?? [0, 0, 0];
  const { ex, ey } = eulerColumns(rotation, 'extrinsic-xyz');
  const ez = cross(ex, ey);
  const d = subtract(point, center);
  return [
    center[0] + ex[0] * d[0] + ey[0] * d[1] + ez[0] * d[2],
    center[1] + ex[1] * d[0] + ey[1] * d[1] + ez[1] * d[2],
    center[2] + ex[2] * d[0] + ey[2] * d[1] + ez[2] * d[2]
  ];
}

/**
 * A frame stored in the model's own coordinates, as the optimizer must
 * receive it: with the model orientation on, the geometry reaches the
 * optimizer turned, and the frame has to turn with it.
 */
export function turnFramePoints(points: LocalFramePoints, orientation: ModelOrientation | null | undefined): LocalFramePoints {
  return {
    origin: turnPoint(points.origin, orientation),
    pointPx: turnPoint(points.pointPx, orientation),
    pointPy: turnPoint(points.pointPy, orientation)
  };
}

/**
 * `userCoordinateSystem.inputFrame` for a project whose loads, supports and
 * points are stored in the model's own orientation, as imported. The run turns
 * them with the geometry (see `solverInputs`). Projects saved before carry no
 * value and store them in the turned frame.
 */
export const MODEL_INPUT_FRAME = 'model';

/** The project's model orientation together with the frame its inputs are stored in. */
export interface InputOrientation extends ModelOrientation {
  inputFrame?: string | null;
}

/** Rows of a 3x3 rotation. */
type Mat3 = [Vec3, Vec3, Vec3];

function rotationOf(orientation: ModelOrientation | null | undefined): Mat3 {
  const unturned = { ...orientation, origin: [0, 0, 0] };
  const ex = turnPoint([1, 0, 0], unturned);
  const ey = turnPoint([0, 1, 0], unturned);
  const ez = turnPoint([0, 0, 1], unturned);
  return [
    [ex[0], ey[0], ez[0]],
    [ex[1], ey[1], ez[1]],
    [ex[2], ey[2], ez[2]]
  ];
}

function multiply(a: Mat3, b: Mat3): Mat3 {
  const row = (i: number): Vec3 => [0, 1, 2].map((j) => a[i][0] * b[0][j] + a[i][1] * b[1][j] + a[i][2] * b[2][j]) as Vec3;
  return [row(0), row(1), row(2)];
}

function transpose(m: Mat3): Mat3 {
  return [
    [m[0][0], m[1][0], m[2][0]],
    [m[0][1], m[1][1], m[2][1]],
    [m[0][2], m[1][2], m[2][2]]
  ];
}

function rotate(m: Mat3, v: number[]): Vec3 {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2]
  ];
}

function isIdentity(m: Mat3): boolean {
  return m.every((row, i) => row.every((value, j) => Math.abs(value - (i === j ? 1 : 0)) < 1e-12));
}

// Turned values are rounded so a turn by a right angle gives back clean
// numbers rather than 6e-17 noise; nanometres and nano-newtons are far below
// anything the solver resolves.
function clean(value: number): number {
  return Math.round(value * 1e9) / 1e9 + 0;
}

function isVec3(value: unknown): value is number[] {
  return Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === 'number' && Number.isFinite(n));
}

/** Moves values from one model orientation's frame to another's. */
class FrameChange {
  readonly rotation: Mat3;
  private readonly previous: ModelOrientation | null | undefined;
  private readonly next: ModelOrientation | null | undefined;
  private readonly undoPrevious: Mat3;

  constructor(previous: ModelOrientation | null | undefined, next: ModelOrientation | null | undefined) {
    this.previous = previous;
    this.next = next;
    this.undoPrevious = transpose(rotationOf(previous));
    this.rotation = multiply(rotationOf(next), this.undoPrevious);
  }

  get isNone(): boolean {
    const probe: Vec3 = [1, 2, 3];
    const moved = this.point(probe);
    return isIdentity(this.rotation) && moved.every((value, i) => Math.abs(value - probe[i]) < 1e-9);
  }

  direction(value: number[]): number[] {
    // A direction with a cleared field still turns; the empty component is 0.
    const filled = Array.isArray(value) && value.length === 3 ? value.map((n) => n ?? 0) : value;
    return isVec3(filled) ? rotate(this.rotation, filled).map(clean) : value;
  }

  point(value: number[]): number[] {
    if (!isVec3(value)) return value;
    // Back to the model's own coordinates, then into the next frame.
    let model: Vec3 = [value[0], value[1], value[2]];
    if (this.previous?.enabled) {
      const center = (isVec3(this.previous.origin) ? this.previous.origin : [0, 0, 0]) as Vec3;
      const back = rotate(this.undoPrevious, [model[0] - center[0], model[1] - center[1], model[2] - center[2]]);
      model = [back[0] + center[0], back[1] + center[1], back[2] + center[2]];
    }
    return turnPoint(model, this.next).map(clean);
  }

  pointObject(value: { x: number; y: number; z: number }): void {
    const [x, y, z] = this.point([value.x, value.y, value.z]);
    Object.assign(value, { x, y, z });
  }

  /** [I11, I22, I33, I23, I13, I12] turned as R·I·Rᵀ. */
  inertia(value: number[]): number[] {
    if (!Array.isArray(value) || value.length !== 6) return value;
    const [i11, i22, i33, i23, i13, i12] = value;
    const tensor: Mat3 = [
      [i11, i12, i13],
      [i12, i22, i23],
      [i13, i23, i33]
    ];
    const t = multiply(multiply(this.rotation, tensor), transpose(this.rotation));
    return [t[0][0], t[1][1], t[2][2], t[1][2], t[0][2], t[0][1]].map(clean);
  }
}

interface FramedLoad {
  remotePoint?: number[];
  centerOfMass?: number[];
  forceComponent?: number[];
  momentComponent?: number[];
  inertiaTensor?: number[];
  referencePoint?: number[] | null;
  coordinateSystemId?: string | null;
}

interface FramedBoundaryCondition {
  fixedX?: boolean;
  fixedY?: boolean;
  fixedZ?: boolean;
  rotationAlongAxis?: boolean;
  rotationAxisX?: number;
  rotationAxisY?: number;
  rotationAxisZ?: number;
  coordinateSystemId?: string | null;
}

export interface RealignableLoadcase {
  loads?: FramedLoad[];
  groups?: FramedLoad[];
  boundaryConditions?: FramedBoundaryCondition[];
  acceleration?: { accelerationDirectionVector?: number[] };
  excitedSupports?: { direction?: number[] }[];
  deformationPoints?: { x: number; y: number; z: number }[];
}

export interface RealignableProject {
  loadcases?: RealignableLoadcase[];
  goal?: { centerOfGravity?: { x: number; y: number; z: number } };
  coordinateSystems?: CoordinateSystem[];
  manufacturingMethods?: {
    variants?: { loadcaseOverrides?: Record<string, { deformationPoints?: { x: number; y: number; z: number }[] }> }[];
  }[];
}

/**
 * The frame whose axes are the previous orientation's global axes, written in
 * the model's own coordinates. Reused when one with the same name and axes is
 * already there.
 */
function frameAlongPreviousAxes(project: RealignableProject, previous: ModelOrientation | null | undefined, name: string): CoordinateSystem {
  const systems = (project.coordinateSystems ??= []);
  const back = transpose(rotationOf(previous));
  const axes: LocalFrameAxes = {
    ex: [back[0][0], back[1][0], back[2][0]].map(clean) as Vec3,
    ey: [back[0][1], back[1][1], back[2][1]].map(clean) as Vec3,
    ez: [back[0][2], back[1][2], back[2][2]].map(clean) as Vec3
  };
  const sameAxes = (candidate: CoordinateSystem): boolean => {
    const other = deriveAxesFromDefinition(candidate.definition);
    if (!other) return false;
    return (['ex', 'ey', 'ez'] as const).every((key) => other[key].every((value, i) => Math.abs(value - axes[key][i]) < 1e-5));
  };
  const existing = systems.find((candidate) => candidate.name.startsWith(name) && sameAxes(candidate));
  if (existing) return existing;

  const taken = new Set(systems.map((candidate) => candidate.name.trim().toLowerCase()));
  let unique = name;
  for (let index = 2; taken.has(unique.toLowerCase()); index++) unique = `${name} ${index}`;
  const system: CoordinateSystem = {
    id: `cs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: unique,
    definition: { mode: 'points', origin: [0, 0, 0], pointPx: [...axes.ex], pointPy: [...axes.ey] }
  };
  systems.push(system);
  return system;
}

export interface RealignSummary {
  /** Boundary conditions given a frame along the previous axes. */
  framedBoundaryConditions: number;
}

/**
 * Rewrites the project's loads, supports and points from the `previous` model
 * orientation's frame to the `next` one's, in place, so they stay where they
 * were on the part.
 *
 * - Directions and points are turned with the part.
 * - Loads and supports that already use a coordinate system are left alone:
 *   systems are stored in the model's own coordinates.
 * - A support fixed on one or two axes cannot be turned, because its axes are
 *   on/off switches; it is given a frame along the axes it was set on, named
 *   `frameName`. One fixed on all three axes, or none, needs no frame.
 */
export function realignInputs(
  project: RealignableProject,
  previous: ModelOrientation | null | undefined,
  next: ModelOrientation | null | undefined,
  frameName: string
): RealignSummary {
  const summary: RealignSummary = { framedBoundaryConditions: 0 };
  const change = new FrameChange(previous, next);
  if (change.isNone) return summary;

  const moveLoad = (load: FramedLoad): void => {
    if (isVec3(load.remotePoint)) load.remotePoint = change.point(load.remotePoint);
    if (isVec3(load.centerOfMass)) load.centerOfMass = change.point(load.centerOfMass);
    if (isVec3(load.referencePoint)) load.referencePoint = change.point(load.referencePoint);
    if (load.inertiaTensor) load.inertiaTensor = change.inertia(load.inertiaTensor);
    if (normalizeCoordinateSystemId(load.coordinateSystemId)) return;
    if (load.forceComponent) load.forceComponent = change.direction(load.forceComponent);
    if (load.momentComponent) load.momentComponent = change.direction(load.momentComponent);
  };

  for (const loadcase of project.loadcases ?? []) {
    loadcase.loads?.forEach(moveLoad);
    loadcase.groups?.forEach(moveLoad);

    for (const bc of loadcase.boundaryConditions ?? []) {
      if (bc.rotationAlongAxis) {
        const [x, y, z] = change.direction([bc.rotationAxisX ?? 0, bc.rotationAxisY ?? 0, bc.rotationAxisZ ?? 0]);
        Object.assign(bc, { rotationAxisX: x, rotationAxisY: y, rotationAxisZ: z });
        continue;
      }
      if (normalizeCoordinateSystemId(bc.coordinateSystemId)) continue;
      const fixed = [bc.fixedX, bc.fixedY, bc.fixedZ].filter(Boolean).length;
      if (fixed === 0 || fixed === 3) continue;
      bc.coordinateSystemId = frameAlongPreviousAxes(project, previous, frameName).id;
      summary.framedBoundaryConditions++;
    }

    const acceleration = loadcase.acceleration;
    if (acceleration?.accelerationDirectionVector) {
      acceleration.accelerationDirectionVector = change.direction(acceleration.accelerationDirectionVector);
    }
    for (const support of loadcase.excitedSupports ?? []) {
      if (support.direction) support.direction = change.direction(support.direction);
    }
    loadcase.deformationPoints?.forEach((point) => change.pointObject(point));
  }

  for (const method of project.manufacturingMethods ?? []) {
    for (const variant of method.variants ?? []) {
      for (const override of Object.values(variant.loadcaseOverrides ?? {})) {
        override.deformationPoints?.forEach((point) => change.pointObject(point));
      }
    }
  }

  if (project.goal?.centerOfGravity) change.pointObject(project.goal.centerOfGravity);

  return summary;
}

/** Name of the frame the run gives supports fixed on one or two of the model's own axes. */
export const MODEL_AXES_FRAME_NAME = 'Model axes';

/** Name of the frame a support fixed on one or two of the turned axes gets when its inputs are moved to the model frame. */
export const TURNED_AXES_FRAME_NAME = 'Model orientation axes';

/** `runType` of an FEA run (RunType.FiniteElementAnalysis). */
const FEA_RUN_TYPE = 1;

/**
 * The project as the optimizer must receive it.
 *
 * - An FEA run has no manufacturing axes to line up with, so it solves the
 *   model as imported: the copy has the model orientation off and every load,
 *   support and point in the model's own frame, moved there from the turned
 *   frame for projects saved before inputs were stored in the model frame.
 * - Otherwise, with the model orientation on and the inputs stored in the
 *   model's own orientation, the run turns the geometry, so the copy has the
 *   loads, supports and points turned the same way.
 *
 * Anything else is returned as it is.
 */
export function solverInputs<
  T extends RealignableProject & { userCoordinateSystem?: InputOrientation | null; runType?: unknown }
>(config: T): T {
  const ucs = config.userCoordinateSystem;
  if (!ucs?.enabled) return config;
  const storedInModelFrame = ucs.inputFrame === MODEL_INPUT_FRAME;
  if (config.runType === FEA_RUN_TYPE) {
    const copy = JSON.parse(JSON.stringify(config)) as T;
    if (!storedInModelFrame) realignInputs(copy, ucs, null, TURNED_AXES_FRAME_NAME);
    const off = copy.userCoordinateSystem as InputOrientation;
    off.enabled = false;
    off.inputFrame = MODEL_INPUT_FRAME;
    return copy;
  }
  if (!storedInModelFrame) return config;
  const copy = JSON.parse(JSON.stringify(config)) as T;
  realignInputs(copy, null, ucs, MODEL_AXES_FRAME_NAME);
  return copy;
}
